// A counterparty's comments on its profile (TX-US-0037): what was asked and answered while it
// is onboarded. Operations, Compliance and Risk add one whatever its status; everyone who reads
// the counterparty reads them. A comment is not edited and not deleted.
//
// Newest first, twenty to a page. The pages are keyset, as the registers are: Next is the cursor
// the page gave, Previous the one before it (usePageTrail.js). A comment just added is on the
// first page, so adding one returns there.
//
// The panel folds away under its heading and opens unfolded. Folded, it keeps what it holds: a
// comment being typed and the page being read are there when it is opened again.
//
// The API is the authority on who may write - `canComment` only decides what is offered.

import { useEffect, useState } from 'react';

import { addCounterpartyComment, pageCounterpartyComments } from '../../api.js';
import Pager from '../../ui/Pager.jsx';
import usePageTrail from '../../ui/usePageTrail.js';
import { MAX_COMMENT_LENGTH, authorRoles } from './comments.js';

const when = (iso) => new Date(iso).toLocaleString();

export default function CounterpartyComments({ counterpartyId, canComment }) {
  // `reads` counts the reads asked for, so the first page is read again after an add.
  const [view, setView] = useState({ cursor: null, reads: 0 });
  const [page, setPage] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const trail = usePageTrail({
    query: view,
    page,
    apply: setView,
    withCursor: (current, cursor) => ({ ...current, cursor }),
  });

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    pageCounterpartyComments(counterpartyId, view.cursor)
      .then((found) => {
        if (!cancelled) setPage(found);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [counterpartyId, view]);

  const add = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await addCounterpartyComment(counterpartyId, text.trim());
      setText('');
      trail.reset();
      setView((current) => ({ cursor: null, reads: current.reads + 1 }));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const comments = page?.comments ?? [];
  const paged = trail.hasPrevious || Boolean(page?.has_more);

  return (
    <details className="counterparty-comments" open>
      <summary><h3>Comments</h3></summary>

      {error && <p className="error">{error}</p>}

      {canComment && (
        <form className="comment-form" onSubmit={add}>
          <textarea
            aria-label="Add a comment"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Add a comment"
            rows={3}
            maxLength={MAX_COMMENT_LENGTH}
          />
          <button type="submit" disabled={busy || !text.trim()}>Add comment</button>
        </form>
      )}

      {page && comments.length === 0 && <p className="muted">No comments.</p>}

      {comments.length > 0 && (
        <ol className="comment-list" aria-label="Comments">
          {comments.map((comment) => (
            <li key={comment.comment_id}>
              <header>
                <strong title={comment.created_by}>{comment.author_name}</strong>
                <span className="tag">{authorRoles(comment)}</span>
                <time dateTime={comment.created_at}>{when(comment.created_at)}</time>
              </header>
              <p>{comment.body}</p>
            </li>
          ))}
        </ol>
      )}

      {paged && (
        <Pager busy={busy} hasPrevious={trail.hasPrevious} hasMore={Boolean(page?.has_more)}
               onPrevious={trail.onPrevious} onNext={trail.onNext} />
      )}
    </details>
  );
}
